import React,{useState,useEffect,useCallback} from 'react';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import SlidePanel from '../components/common/SlidePanel';
import Badge from '../components/common/Badge';
import {Select} from '../components/common/FormField';
import api from '../services/api';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import {fmtDate,fmtDateTime,fmtCurrency} from '../utils/helpers';

// ─── constants ────────────────────────────────────────────────────────────────
const ORDER_TYPE_ICON:Record<string,string>={LAB:'🧪',RADIOLOGY:'🩻',PHARMACY:'💊',PROCEDURE:'🔪'};
const ORDER_TYPE_CSS:Record<string,string>={LAB:'bg-amber-50 text-amber-700',RADIOLOGY:'bg-purple-50 text-purple-700',PHARMACY:'bg-green-50 text-green-700',PROCEDURE:'bg-pink-50 text-pink-700'};
const PRIORITY_CSS:Record<string,string>={STAT:'bg-red-600 text-white',EMERGENCY:'bg-red-600 text-white',URGENT:'bg-amber-100 text-amber-700',ROUTINE:'bg-slate-100 text-slate-500'};
const PAYMENT_METHODS=[['CASH','💵 Cash'],['CARD','💳 Card'],['UPI','📱 UPI'],['CREDIT','📋 Credit (Settle Later)'],['INSURANCE','🤝 Insurance / TPA']];

export default function FrontOfficeBillingPage(){
  const [tab,setTab]=useState<'queue'|'onhold'|'settlement'>('queue');
  const [loading,setLoading]=useState(false);
  const [msg,setMsg]=useState('');
  const [saving,setSaving]=useState(false);
  const [dash,setDash]=useState<any>({});
  const [queue,setQueue]=useState<any[]>([]);
  const [onHold,setOnHold]=useState<any[]>([]);
  const [deptFilter,setDeptFilter]=useState('');

  // Selected orders for billing (within one patient group)
  const [selPatientGroup,setSelPatientGroup]=useState<any>(null);
  const [selOrderIds,setSelOrderIds]=useState<Set<string>>(new Set());
  const [showBillModal,setShowBillModal]=useState(false);
  const [discountAmount,setDiscountAmount]=useState('0');
  const [discountReason,setDiscountReason]=useState('');
  const [billResult,setBillResult]=useState<any>(null);
  const defaultPay={cashAmount:0,upiAmount:0,cardAmount:0,neftAmount:0,chequeAmount:0,creditAmount:0,insuranceAmount:0,upiRef:'',cardLast4:'',bankName:'',reference:''};
  const [splitPay,setSplitPay]=useState<any>({...defaultPay});
  const payTotal=()=>Object.entries(splitPay).filter(([k])=>k.endsWith('Amount')).reduce((s,[,v])=>s+(+(v as any)||0),0);

  // Hold modal
  const [showHoldModal,setShowHoldModal]=useState(false);
  const [holdReason,setHoldReason]=useState('Patient informed for payment');
  const [holdGroup,setHoldGroup]=useState<any>(null);
  const [holdOrderIds,setHoldOrderIds]=useState<Set<string>>(new Set());

  // Visit Settlement
  const [selPatient,setSelPatient]=useState<any>(null);
  const [visitSummary,setVisitSummary]=useState<any>(null);
  const [selBillIds,setSelBillIds]=useState<Set<string>>(new Set());
  const [settlePaymentMethod,setSettlePaymentMethod]=useState('CASH');

  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),5000);};

  useEffect(()=>{
    api.getFOBDashboard().then(r=>setDash(r.data||{})).catch(()=>{});
  },[]);

  const loadTab=useCallback(async()=>{
    setLoading(true);
    try{
      if(tab==='queue'){const r=await api.getFOBQueue(deptFilter||undefined);setQueue(r.data?.queue||[]);}
      if(tab==='onhold'){const r=await api.getFOBOnHold();setOnHold(r.data||[]);}
    }catch{}finally{setLoading(false);}
  },[tab,deptFilter]);
  useEffect(()=>{loadTab();},[loadTab]);

  const toggleOrder=(id:string)=>{
    setSelOrderIds(prev=>{const next=new Set(prev);next.has(id)?next.delete(id):next.add(id);return next;});
  };

  const openBillModal=(group:any)=>{
    setSelPatientGroup(group);
    setSelOrderIds(new Set(group.orders.map((o:any)=>o.id)));
    setDiscountAmount('0');setDiscountReason('');setBillResult(null);
    setShowBillModal(true);
  };

  const selectedTotal=()=>{
    if(!selPatientGroup)return 0;
    return selPatientGroup.orders.filter((o:any)=>selOrderIds.has(o.id)).reduce((s:number,o:any)=>s+(+o.estimated_amount||0),0);
  };

  const submitBilling=async()=>{
    if(!selPatientGroup||!selOrderIds.size)return;
    setSaving(true);
    try{
      const modes=['cashAmount','upiAmount','cardAmount','neftAmount','chequeAmount','creditAmount','insuranceAmount'];
      const primaryMode=modes.find(k=>+(splitPay[k]||0)>0)?.replace('Amount','').toUpperCase()||'CASH';
      const totalPaying=payTotal();
      const r=await api.billAndAuthorizeOrders({orderIds:Array.from(selOrderIds),patientId:selPatientGroup.patientId,
        paymentMethod:totalPaying>0?primaryMode:'CASH',paymentSplit:splitPay,
        discountAmount:+discountAmount,discountReason});
      setBillResult(r.data);
      notify(r.data?.status==='AUTHORIZED'?`✅ Billed & Authorized — orders now visible to departments!`:`✅ Billed — pending payment confirmation`);
      loadTab();api.getFOBDashboard().then(x=>setDash(x.data||{}));
    }catch(ex:any){notify('❌ '+ex.response?.data?.message);}finally{setSaving(false);}
  };

  const openHoldModal=(group:any,orderIds?:string[])=>{
    setHoldGroup(group);
    setHoldOrderIds(new Set(orderIds||group.orders.map((o:any)=>o.id)));
    setHoldReason('Patient informed for payment');
    setShowHoldModal(true);
  };

  const submitHold=async()=>{
    if(!holdOrderIds.size)return;setSaving(true);
    try{await api.putOrdersOnHold(Array.from(holdOrderIds),holdReason);notify('⏸ Orders placed ON HOLD — not visible to departments until payment.');setShowHoldModal(false);loadTab();api.getFOBDashboard().then(x=>setDash(x.data||{}));}
    catch(ex:any){notify('❌ '+ex.response?.data?.message);}finally{setSaving(false);}
  };

  const releaseHold=async(orderIds:string[])=>{
    setSaving(true);
    try{await api.releaseOrdersHold(orderIds);notify('✅ Released back to billing queue');loadTab();}
    catch(ex:any){notify('❌ '+ex.response?.data?.message);}finally{setSaving(false);}
  };

  const loadVisitSummary=async(p:any)=>{
    setSelPatient(p);setVisitSummary(null);setSelBillIds(new Set());
    if(!p)return;
    const r=await api.getVisitBillSummary(p.id);
    setVisitSummary(r.data);
    setSelBillIds(new Set((r.data?.bills||[]).filter((b:any)=>b.status!=='PAID').map((b:any)=>b.id)));
  };

  const settleSelected=async()=>{
    if(!selPatient||!selBillIds.size)return;setSaving(true);
    try{
      const r=await api.settleVisitBills({patientId:selPatient.id,billIds:Array.from(selBillIds),paymentMethod:settlePaymentMethod});
      notify(`✅ Settled ${r.data?.settled||0} bills — ₹${fmtCurrency(r.data?.totalCollected||0)} collected`);
      loadVisitSummary(selPatient);
    }catch(ex:any){notify('❌ '+ex.response?.data?.message);}finally{setSaving(false);}
  };

  const TABS=[['queue','📋 Billing Queue'],['onhold','⏸ On Hold'],['settlement','🧾 Final Bill Settlement']];

  return(
    <Layout title="Front Office Billing — Order Authorization Gate" actions={
      tab==='queue'?<Select value={deptFilter} onChange={e=>setDeptFilter(e.target.value)} className="!w-48 text-sm"><option value="">All Order Types</option><option value="LAB">🧪 Laboratory</option><option value="RADIOLOGY">🩻 Radiology</option><option value="PHARMACY">💊 Pharmacy</option><option value="PROCEDURE">🔪 Procedure</option></Select>:undefined
    }>
      {msg&&<div className={`mb-3 p-2.5 rounded-lg text-sm border ${msg.startsWith('✅')||msg.startsWith('⏸')?'bg-green-50 text-green-700 border-green-200':'bg-red-50 text-red-700 border-red-200'}`}>{msg}</div>}

      {/* Workflow explainer */}
      <div className="card p-4 mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-center gap-2 overflow-x-auto text-xs font-semibold text-slate-600">
          <span className="bg-[#02357A] text-white px-2 py-1 rounded-full flex-shrink-0">7 · CPOE Orders</span>
          <span className="text-slate-300">→</span>
          <span className="bg-indigo-600 text-white px-2 py-1 rounded-full flex-shrink-0">8 · Front Office Billing</span>
          <span className="text-slate-300">→</span>
          <span className="bg-green-600 text-white px-2 py-1 rounded-full flex-shrink-0">9 · Lab / Radiology / Pharmacy</span>
          <span className="text-slate-300">→</span>
          <span className="bg-[#039089] text-white px-2 py-1 rounded-full flex-shrink-0">12 · Final Settlement</span>
        </div>
        <p className="text-xs text-slate-500 mt-2">No service execution without billing authorization — orders placed by doctors stay <strong>PENDING BILLING</strong> until billed here. Once paid/authorized, they instantly become visible in the Lab/Radiology/Pharmacy worklists.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard label="Pending Billing" value={dash.pendingBilling||0} icon="📋" color="amber"/>
        <StatCard label="On Hold (Unpaid)" value={dash.onHold||0} icon="⏸" color="red"/>
        <StatCard label="Authorized Today" value={dash.authorizedToday||0} icon="✅" color="green"/>
        <StatCard label="Revenue Today" value={`₹${fmtCurrency(dash.revenueToday||0)}`} icon="💰" color="blue"/>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b">
        {TABS.map(([t,l])=><button key={t} onClick={()=>setTab(t as any)} className={`px-3 py-1.5 rounded-t-lg text-xs font-medium ${tab===t?'bg-[#02357A] text-white':'text-slate-600 hover:bg-slate-100'}${t==='onhold'&&dash.onHold?' ring-2 ring-red-400':''}`}>{l}</button>)}
      </div>

      {/* ════ BILLING QUEUE ════ */}
      {tab==='queue'&&(
        <div className="space-y-4">
          {loading?<p className="text-center text-slate-400 py-8">Loading...</p>:
          !queue.length?<div className="card p-10 text-center text-slate-400"><p className="text-4xl mb-2">✅</p><p>No orders pending billing — everything is authorized!</p></div>:
          queue.map((group:any)=>(
            <div key={group.patientId} className="card overflow-hidden">
              <div className="p-4 border-b bg-slate-50 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-bold flex-shrink-0">{group.patientName?.[0]?.toUpperCase()||'P'}</div>
                  <div><p className="font-bold">{group.patientName}</p><p className="text-xs text-slate-400">{group.uhid} · {group.phone||'—'}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right"><p className="text-xs text-slate-400">{group.orders.length} order(s)</p><p className="font-bold text-lg text-[#02357A]">₹{fmtCurrency(group.totalEstimate)}</p></div>
                  <button onClick={()=>openHoldModal(group)} className="btn-secondary text-xs border-amber-300 text-amber-600 hover:bg-amber-50">⏸ Hold All</button>
                  <button onClick={()=>openBillModal(group)} className="btn-primary text-sm">💳 Bill & Authorize</button>
                </div>
              </div>
              <div className="divide-y">
                {group.orders.map((o:any)=>(
                  <div key={o.id} className="p-3 flex items-center gap-3">
                    <span className="text-lg flex-shrink-0">{ORDER_TYPE_ICON[o.order_type]||'📋'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm">{o.item_name}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${ORDER_TYPE_CSS[o.order_type]||''}`}>{o.order_type}</span>
                        {o.priority&&o.priority!=='ROUTINE'&&<span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${PRIORITY_CSS[o.priority]||''}`}>{o.priority}</span>}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">Dr. {o.doctor_first_name} {o.doctor_last_name||''} · {fmtDateTime(o.created_at)}</p>
                    </div>
                    <span className="font-bold text-sm flex-shrink-0">₹{fmtCurrency(o.estimated_amount||0)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ════ ON HOLD ════ */}
      {tab==='onhold'&&(
        <div className="space-y-3">
          {!onHold.length?<div className="card p-10 text-center text-slate-400"><p className="text-4xl mb-2">✅</p><p>Nothing on hold</p></div>:
          <div className="card overflow-hidden">
            <div className="p-4 border-b bg-red-50"><h3 className="font-semibold text-red-700">⏸ Orders ON HOLD — Not Visible to Departments</h3><p className="text-xs text-red-500 mt-0.5">Patient must be informed and payment collected before these proceed.</p></div>
            <div className="divide-y">
              {onHold.map((o:any)=>(
                <div key={o.id} className="p-3 flex items-center gap-3">
                  <span className="text-lg">{ORDER_TYPE_ICON[o.order_type]||'📋'}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2"><p className="font-semibold text-sm">{o.item_name}</p><span className={`text-xs px-1.5 py-0.5 rounded ${ORDER_TYPE_CSS[o.order_type]||''}`}>{o.order_type}</span></div>
                    <p className="text-xs text-slate-400">{o.first_name} {o.last_name} ({o.uhid}) · {o.hold_reason||'Payment pending'}</p>
                  </div>
                  <button onClick={()=>releaseHold([o.id])} className="text-xs text-[#02357A] hover:underline font-medium">↩ Release to Queue</button>
                </div>
              ))}
            </div>
          </div>}
        </div>
      )}

      {/* ════ FINAL BILL SETTLEMENT ════ */}
      {tab==='settlement'&&(
        <div className="space-y-4">
          <div className="card p-4"><PatientSearchInput label="Select patient for visit settlement" selected={selPatient} onSelect={loadVisitSummary} onClear={()=>{setSelPatient(null);setVisitSummary(null);}}/></div>

          {visitSummary&&(
            <>
              {/* Category breakdown */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(visitSummary.byCategory||{}).map(([cat,amt]:any)=>(
                  <div key={cat} className="card p-3 text-center"><p className="text-xs text-slate-400">{cat}</p><p className="font-bold text-[#02357A]">₹{fmtCurrency(amt)}</p></div>
                ))}
              </div>

              {/* Totals */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="card p-4 text-center border-2 border-blue-200 bg-blue-50"><p className="text-2xl font-black text-[#02357A]">₹{fmtCurrency(visitSummary.totals?.net||0)}</p><p className="text-xs text-[#02357A] mt-1">Total Billed</p></div>
                <div className="card p-4 text-center border-2 border-green-200 bg-green-50"><p className="text-2xl font-black text-green-700">₹{fmtCurrency(visitSummary.totals?.paid||0)}</p><p className="text-xs text-green-600 mt-1">Paid</p></div>
                <div className="card p-4 text-center border-2 border-red-200 bg-red-50"><p className="text-2xl font-black text-red-700">₹{fmtCurrency(visitSummary.totals?.outstanding||0)}</p><p className="text-xs text-red-600 mt-1">Outstanding</p></div>
                <div className="card p-4 text-center border-2 border-amber-200 bg-amber-50"><p className="text-2xl font-black text-amber-700">₹{fmtCurrency(visitSummary.totals?.discount||0)}</p><p className="text-xs text-amber-600 mt-1">Discount Applied</p></div>
              </div>

              {/* Bills list */}
              <div className="card overflow-hidden">
                <div className="p-4 border-b flex items-center justify-between"><h3 className="font-semibold">Consolidated Bills — {selPatient.firstName} {selPatient.lastName||''}</h3>
                  {visitSummary.totals?.outstanding>0&&<div className="flex items-center gap-2">
                    <Select value={settlePaymentMethod} onChange={e=>setSettlePaymentMethod(e.target.value)} className="!w-40 text-xs !py-1.5">{PAYMENT_METHODS.map(([v,l])=><option key={v} value={v}>{l}</option>)}</Select>
                    <button onClick={settleSelected} disabled={saving||!selBillIds.size} className="btn-primary text-xs">{saving?'Settling...':`💳 Settle Selected (${selBillIds.size})`}</button>
                  </div>}
                </div>
                <div className="divide-y">
                  {(visitSummary.bills||[]).map((b:any)=>(
                    <div key={b.id} className="p-3 flex items-center gap-3">
                      {b.status!=='PAID'&&<input type="checkbox" checked={selBillIds.has(b.id)} onChange={()=>setSelBillIds(prev=>{const n=new Set(prev);n.has(b.id)?n.delete(b.id):n.add(b.id);return n;})} className="w-4 h-4 flex-shrink-0"/>}
                      <div className="flex-1"><p className="font-mono text-xs text-slate-500">{b.bill_number}</p><p className="text-sm font-semibold">{b.bill_type} · {b.item_count} item(s)</p><p className="text-xs text-slate-400">{fmtDate(b.bill_date)}</p></div>
                      <div className="text-right"><p className="font-bold">₹{fmtCurrency(b.net_amount)}</p><Badge label={b.status} className={b.status==='PAID'?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}/></div>
                    </div>
                  ))}
                  {!(visitSummary.bills||[]).length&&<p className="p-4 text-sm text-slate-400">No bills found for this patient yet</p>}
                </div>
              </div>
            </>
          )}
          {!selPatient&&<div className="card p-10 text-center text-slate-400"><p className="text-4xl mb-2">🧾</p><p>Search for a patient to view their consolidated visit bill</p></div>}
        </div>
      )}

      {/* ════ BILL & AUTHORIZE SIDE PANEL (no modal) ════ */}
      {showBillModal&&selPatientGroup&&(
        <div className="fixed inset-0 z-40 flex">
          <div className="flex-1 bg-black/30" onClick={()=>{setShowBillModal(false);setBillResult(null);setSplitPay({...defaultPay});}}/>
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
            {/* Header */}
            <div className="p-4 border-b bg-gradient-to-r from-[#02357A] to-blue-900 text-white flex justify-between items-center flex-shrink-0">
              <div>
                <h2 className="font-bold text-lg">💳 Bill & Authorize</h2>
                <p className="text-blue-200 text-sm">{selPatientGroup.patientName} · {selPatientGroup.uhid||''}</p>
              </div>
              <button onClick={()=>{setShowBillModal(false);setBillResult(null);setSplitPay({...defaultPay});}} className="text-white/70 hover:text-white text-2xl">✕</button>
            </div>

            {!billResult?(
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Order list */}
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase mb-2">Select Orders to Bill</p>
                  <div className="space-y-1 border rounded-xl overflow-hidden">
                    {selPatientGroup.orders.map((o:any,i:number)=>(
                      <label key={o.id} className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${selOrderIds.has(o.id)?'bg-blue-50':'hover:bg-slate-50'} ${i>0?'border-t':''}`}>
                        <input type="checkbox" checked={selOrderIds.has(o.id)} onChange={()=>toggleOrder(o.id)} className="w-4 h-4 accent-blue-600"/>
                        <span className="text-lg flex-shrink-0">{ORDER_TYPE_ICON[o.order_type]||'📋'}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{o.item_name}</p>
                          <p className="text-xs text-slate-400">{o.order_type} · {o.priority||'ROUTINE'}</p>
                        </div>
                        <span className="font-bold text-sm">₹{fmtCurrency(o.estimated_amount||0)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Bill summary */}
                <div className="bg-blue-50 rounded-xl p-3 flex justify-between items-center">
                  <div>
                    <p className="text-xs text-blue-500">Selected ({selOrderIds.size} orders)</p>
                    <p className="text-xl font-black text-[#02357A]">₹{fmtCurrency(selectedTotal())}</p>
                  </div>
                  <div className="text-right">
                    <label className="text-xs text-blue-500">Discount (₹)</label>
                    <input type="number" min="0" max={selectedTotal()} value={discountAmount}
                      onChange={e=>setDiscountAmount(e.target.value)}
                      className="block w-24 text-right font-bold text-[#02357A] bg-white border border-blue-200 rounded-lg px-2 py-1 text-sm mt-0.5"/>
                  </div>
                </div>
                {+discountAmount>0&&(
                  <div>
                    <label className="label text-xs">Discount Reason</label>
                    <input value={discountReason} onChange={e=>setDiscountReason(e.target.value)} className="input text-sm" placeholder="e.g. Senior citizen, Staff, Charity..."/>
                  </div>
                )}

                {/* Net payable */}
                <div className="bg-slate-800 rounded-xl p-3 flex justify-between items-center text-white">
                  <span className="font-semibold">Net Payable</span>
                  <span className="text-xl font-black text-green-400">₹{fmtCurrency(selectedTotal()-(+discountAmount||0))}</span>
                </div>

                {/* Split payment modes */}
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase mb-2">💰 Payment — Split across modes</p>
                  <div className="space-y-2">
                    {([
                      {f:'cashAmount',   ic:'💵', lb:'Cash',                  credit:false},
                      {f:'upiAmount',    ic:'📱', lb:'UPI / GPay / PhonePe',  credit:false, rf:'upiRef',   rl:'UPI Reference No.'},
                      {f:'cardAmount',   ic:'💳', lb:'Debit / Credit Card',   credit:false, rf:'cardLast4',rl:'Card Last 4 Digits'},
                      {f:'neftAmount',   ic:'🔄', lb:'NEFT / RTGS / IMPS',    credit:false, rf:'reference',rl:'Transaction Reference'},
                      {f:'chequeAmount', ic:'🏦', lb:'Cheque / DD',           credit:false, rf:'reference',rl:'Cheque Number'},
                      {f:'creditAmount', ic:'📋', lb:'Credit (Settle Later)', credit:true},
                      {f:'insuranceAmount',ic:'🤝',lb:'Insurance / TPA',      credit:true},
                    ] as any[]).map(mode=>{
                      const val=+(splitPay[mode.f]||0);
                      const netBill=selectedTotal()-(+discountAmount||0);
                      const otherTotal=(['cashAmount','upiAmount','cardAmount','neftAmount','chequeAmount','creditAmount','insuranceAmount'] as const).filter(k=>k!==mode.f).reduce((s,k)=>s+(+(splitPay[k]||0)),0);
                      const remaining=Math.max(0,netBill-otherTotal);
                      return(
                        <div key={mode.f} className={`rounded-xl border-2 overflow-hidden transition-all ${val>0?'border-blue-400':'border-slate-200'}`}>
                          <div className="flex items-center gap-2 p-2.5 bg-white">
                            <span className="text-lg">{mode.ic}</span>
                            <span className="flex-1 text-sm font-medium text-slate-700">{mode.lb}</span>
                            {mode.credit&&<span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Auth now, pay later</span>}
                            {val===0&&remaining>0&&!mode.credit&&<div className="flex gap-1">
                              {[500,1000,2000].filter(v=>v<=remaining+1).map(v=>(
                                <button key={v} type="button" onClick={()=>setSplitPay({...splitPay,[mode.f]:Math.min(v,remaining)})}
                                  className="text-xs bg-slate-100 hover:bg-blue-50 hover:text-[#02357A] px-1.5 py-1 rounded">₹{v>=1000?v/1000+'K':v}</button>
                              ))}
                              <button type="button" onClick={()=>setSplitPay({...splitPay,[mode.f]:Math.round(remaining*100)/100})}
                                className="text-xs bg-blue-100 text-[#02357A] px-2 py-1 rounded font-semibold">Full</button>
                            </div>}
                            <div className="flex items-center gap-1">
                              <span className="text-slate-400 text-xs">₹</span>
                              <input type="number" min="0" step="0.01" placeholder="0"
                                className={`w-24 text-right font-bold text-sm px-2 py-1.5 border-2 rounded-lg outline-none ${val>0?'border-blue-400 text-[#02357A]':'border-slate-200'}`}
                                value={val||''}
                                onChange={e=>setSplitPay({...splitPay,[mode.f]:+e.target.value})}/>
                              {val>0&&<button type="button" onClick={()=>setSplitPay({...splitPay,[mode.f]:0})} className="text-slate-400 hover:text-red-500 font-bold">×</button>}
                            </div>
                          </div>
                          {val>0&&mode.rf&&(
                            <div className="px-3 pb-2 bg-slate-50 border-t pt-1.5">
                              <label className="text-xs text-slate-400">{mode.rl}</label>
                              <input className="input w-full text-xs mt-0.5" value={splitPay[mode.rf]||''} onChange={e=>setSplitPay({...splitPay,[mode.rf]:e.target.value})} placeholder={mode.rl}/>
                              {(mode.f==='cardAmount'||mode.f==='chequeAmount')&&<input className="input w-full text-xs mt-1" value={splitPay.bankName||''} onChange={e=>setSplitPay({...splitPay,bankName:e.target.value})} placeholder="Bank Name"/>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Live total */}
                {payTotal()>0&&(()=>{
                  const net=selectedTotal()-(+discountAmount||0);
                  const total=payTotal();
                  const change=total-net;
                  return(
                    <div className="bg-slate-800 rounded-xl p-3 text-white text-sm space-y-1">
                      <div className="flex justify-between font-bold"><span>Collecting</span><span className="text-green-400">₹{fmtCurrency(total)}</span></div>
                      {change>0.01&&<div className="flex justify-between text-amber-300 text-xs"><span>Change to Return</span><span>₹{fmtCurrency(change)}</span></div>}
                      {total<net-0.01&&<div className="flex justify-between text-red-300 text-xs"><span>Balance Due</span><span>₹{fmtCurrency(net-total)}</span></div>}
                    </div>
                  );
                })()}
              </div>
            ):(
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-4">
                <p className="text-6xl">✅</p>
                <p className="font-bold text-xl text-green-700">Authorized!</p>
                <p className="text-slate-600">Bill <span className="font-mono font-semibold text-[#02357A]">{billResult.bill?.billNumber}</span> created</p>
                <p className="text-sm text-slate-500">{billResult.ordersProcessed} order(s) authorized — now visible in Lab / Radiology / Pharmacy worklists</p>
                <button onClick={()=>{setShowBillModal(false);setBillResult(null);setSplitPay({...defaultPay});}} className="btn-primary w-full mt-4">✓ Done</button>
              </div>
            )}

            {/* Footer action */}
            {!billResult&&(
              <div className="p-4 border-t bg-white flex-shrink-0 space-y-2">
                <button onClick={submitBilling} disabled={saving||!selOrderIds.size}
                  className="btn-primary w-full py-3 text-base font-semibold">
                  {saving?'Processing...':`💳 Bill & Authorize (${selOrderIds.size} orders)`}
                </button>
                <p className="text-center text-xs text-slate-400">Orders authorized immediately → departments can proceed with service</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ════ HOLD MODAL ════ */}
      <SlidePanel isOpen={showHoldModal} onClose={()=>setShowHoldModal(false)} title="Place Orders ON HOLD" size="sm">
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">⚠️ Orders will stay invisible to Lab/Radiology/Pharmacy until released and billed. No service execution while on hold.</div>
          <div><label className="label">Reason</label><input value={holdReason} onChange={e=>setHoldReason(e.target.value)} className="input"/></div>
          <p className="text-sm text-slate-500">{holdOrderIds.size} order(s) will be held for <strong>{holdGroup?.patientName}</strong></p>
          <div className="flex gap-3"><button onClick={submitHold} disabled={saving} className="btn-secondary border-red-300 text-red-600 hover:bg-red-50 flex-1">{saving?'Holding...':'⏸ Confirm Hold'}</button><button onClick={()=>setShowHoldModal(false)} className="btn-secondary">Cancel</button></div>
        </div>
      </SlidePanel>
    </Layout>
  );
}
